/**
 * Red counter on the browser-tab favicon (like JivoChat): "N dialogs need the operator".
 * The base icon is read from <link rel="icon">, drawn on a canvas with the badge and put
 * back as a data: URL; count 0 restores the original href.
 */

const CANVAS = 64
const BADGE_BG = '#ff4d4f'
const BADGE_FG = '#ffffff'

let baseHref: string | null = null
let baseType: string | null = null
let baseImage: HTMLImageElement | null = null
let baseLoading: Promise<HTMLImageElement | null> | null = null
let rendered: string | null = null

function iconLink(): HTMLLinkElement | null {
  if (typeof document === 'undefined') return null
  const links = Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]'))
  let link = links.find((l) => !/apple-touch-icon/i.test(l.rel)) ?? null
  if (!link) {
    link = document.createElement('link')
    link.rel = 'icon'
    document.head.appendChild(link)
  }
  if (baseHref == null) {
    baseHref = link.getAttribute('href') || ''
    baseType = link.getAttribute('type')
  }
  return link
}

function loadBase(): Promise<HTMLImageElement | null> {
  if (baseImage) return Promise.resolve(baseImage)
  if (baseLoading) return baseLoading
  const href = baseHref || ''
  if (!href) return Promise.resolve(null)
  baseLoading = new Promise((resolve) => {
    const img = new Image()
    img.onload = () => {
      baseImage = img
      resolve(img)
    }
    img.onerror = () => resolve(null)
    img.src = href
  })
  return baseLoading
}

function badgeLabel(count: number): string {
  return count > 99 ? '99+' : String(count)
}

function draw(img: HTMLImageElement | null, label: string): string | null {
  const canvas = document.createElement('canvas')
  canvas.width = CANVAS
  canvas.height = CANVAS
  const ctx = canvas.getContext('2d')
  if (!ctx) return null
  if (img && img.naturalWidth > 0 && img.naturalHeight > 0) {
    // contain-fit the logo into the square
    const scale = Math.min(CANVAS / img.naturalWidth, CANVAS / img.naturalHeight)
    const w = img.naturalWidth * scale
    const h = img.naturalHeight * scale
    ctx.drawImage(img, (CANVAS - w) / 2, (CANVAS - h) / 2, w, h)
  }
  // Bottom-left badge (donor layout), no outline: at 16px a white ring just eats the red
  // and makes the badge look smaller. Radius ~ 3/8 of the icon so the digit survives shrinking.
  const r = label.length > 2 ? 25 : 24
  const cx = r
  const cy = CANVAS - r
  ctx.beginPath()
  ctx.arc(cx, cy, r, 0, Math.PI * 2)
  ctx.fillStyle = BADGE_BG
  ctx.fill()
  ctx.fillStyle = BADGE_FG
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  const px = label.length > 2 ? 21 : label.length > 1 ? 28 : 34
  ctx.font = `800 ${px}px -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif`
  ctx.fillText(label, cx, cy + 1)
  try {
    return canvas.toDataURL('image/png')
  } catch {
    return null
  }
}

/** Show `count` on the tab icon; 0 (or less) restores the plain icon. Idempotent. */
export function setFaviconBadge(count: number): void {
  const link = iconLink()
  if (!link) return
  const n = Math.max(0, Math.trunc(Number(count) || 0))
  const key = n > 0 ? `badge:${badgeLabel(n)}` : 'base'
  if (key === rendered) return
  rendered = key
  if (n === 0) {
    if (baseHref != null) link.setAttribute('href', baseHref)
    if (baseType) link.setAttribute('type', baseType)
    else link.removeAttribute('type')
    return
  }
  const label = badgeLabel(n)
  void loadBase().then((img) => {
    if (rendered !== key) return // a newer count won
    const url = draw(img, label)
    if (!url) return
    link.setAttribute('type', 'image/png')
    link.setAttribute('href', url)
  })
}
